import React, { useEffect, useRef, useState } from "react";

// This component owns presentation only. Scheduling state and actions stay with the Planner.
export default function ScheduleWorkspace({
  isMobile = false, isTablet = false, staffPool, children,
  selectedShiftPanel, selectedShiftId, onCloseSelectedShift,
  staffPoolRequest = 0,
}) {
  const narrow = isMobile || isTablet;
  const [staffOpen, setStaffOpen] = useState(!narrow);
  const staffRef = useRef(null);
  const shiftRef = useRef(null);
  const toggleRef = useRef(null);
  const closeSelectedRef = useRef(onCloseSelectedShift);
  closeSelectedRef.current = onCloseSelectedShift;
  const hasSelection = Boolean(selectedShiftPanel);

  useEffect(() => { if (narrow) setStaffOpen(false); }, [narrow]);
  useEffect(() => { if (selectedShiftId) setStaffOpen(false); }, [selectedShiftId]);
  useEffect(() => { if (staffPoolRequest) setStaffOpen(true); }, [staffPoolRequest]);

  // Drawers retain their children while hidden, including worker search and expansion state.
  useEffect(() => {
    if (!narrow || (!staffOpen && !hasSelection)) return undefined;
    const panel = staffOpen ? staffRef.current : shiftRef.current;
    const previous = document.activeElement;
    const focusable = () => [...panel.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), summary, [href], [tabindex="0"]')]
      .filter(node => {
        for (let parent = node; parent && parent !== panel; parent = parent.parentElement) {
          if (parent.hidden || parent.hasAttribute('inert') || window.getComputedStyle(parent).display === 'none') return false;
          if (parent.tagName === 'DETAILS' && !parent.open && !parent.querySelector('summary')?.contains(node)) return false;
        }
        return true;
      });
    panel.focus();
    const keydown = event => {
      if (event.key === 'Escape') {
        event.preventDefault(); event.stopImmediatePropagation();
        if (staffOpen) setStaffOpen(false); else closeSelectedRef.current?.();
      }
      if (event.key === 'Tab') {
        const nodes = focusable();
        const first = nodes[0], last = nodes[nodes.length - 1];
        if (!first) { event.preventDefault(); panel.focus(); }
        else if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === panel)) {
          event.preventDefault(); first.focus();
        }
      }
    };
    document.addEventListener('keydown', keydown, true);
    return () => {
      document.removeEventListener('keydown', keydown, true);
      if (previous?.isConnected && previous !== document.body && !previous.closest('[hidden], [inert]')) previous.focus();
      else toggleRef.current?.focus();
    };
  }, [narrow, staffOpen, hasSelection]);

  const modalOpen = narrow && (staffOpen || hasSelection);
  return (
    <div className={`schedule-workspace ${staffOpen ? 'schedule-pool-open' : ''} ${hasSelection ? 'schedule-has-selection' : ''}`}
      data-layout={isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop'}>
      <div className="schedule-workspace-tools" inert={modalOpen || undefined}>
        <button ref={toggleRef} type="button" className="schedule-panel-toggle"
          aria-label={staffOpen ? 'Collapse Staff Pool' : 'Open Staff Pool'} aria-expanded={staffOpen} aria-controls="planner-staff-pool"
          onClick={() => setStaffOpen(open => !open)}>
          <span aria-hidden="true">▥</span> {staffOpen ? 'Collapse Staff Pool' : 'Open Staff Pool'}
          <span aria-hidden="true">{staffOpen ? '‹' : '›'}</span>
        </button>
      </div>
      {modalOpen && <div className="schedule-drawer-backdrop" aria-hidden="true" onClick={() => {
        if (staffOpen) setStaffOpen(false); else onCloseSelectedShift?.();
      }} />}
      <aside id="planner-staff-pool" ref={staffRef} hidden={!staffOpen} tabIndex={-1}
        className="schedule-staff-panel" role={narrow && staffOpen ? 'dialog' : undefined}
        aria-modal={narrow && staffOpen ? true : undefined} aria-label="Staff Pool">
        <header className="schedule-panel-heading"><h2>Staff Pool</h2>
          <button type="button" aria-label="Close Staff Pool" onClick={() => { setStaffOpen(false); toggleRef.current?.focus(); }}>×</button>
        </header>
        <div className="schedule-staff-content">{staffPool}</div>
      </aside>
      <section className="schedule-main" aria-label="Schedule workspace" inert={modalOpen || undefined}>{children}</section>
      {hasSelection && <aside ref={shiftRef} className="schedule-selected-panel" tabIndex={-1}
        hidden={narrow && staffOpen} role={narrow ? 'dialog' : undefined}
        aria-modal={narrow && !staffOpen ? true : undefined} aria-label="Selected shift">
        <header className="schedule-panel-heading"><h2>Selected shift</h2>
          <button type="button" aria-label="Close selected shift" onClick={onCloseSelectedShift}>×</button>
        </header>
        <button type="button" className="schedule-panel-toggle" onClick={() => setStaffOpen(true)}
          aria-controls="planner-staff-pool" aria-expanded={staffOpen}>▥ Open Staff Pool</button>
        {selectedShiftPanel}
      </aside>}
    </div>
  );
}
